import { useEffect } from 'react'
import { SITE_CONFIG } from '@/lib/constants'
import { DEFAULT_OG_IMAGE, DEFAULT_ROBOTS, DEFAULT_SEO_KEYWORDS } from '@/seo/resolveSeo'

export interface SEOHeadProps {
  title?: string
  description?: string
  keywords?: string
  image?: string
  url?: string
  type?: string
  robots?: string
  favicon?: string
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
}

const JSON_LD_SCRIPT_PREFIX = 'seo-head-jsonld'

function normalizeJsonLdBlocks(
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
): Record<string, unknown>[] {
  if (!jsonLd) return []
  return Array.isArray(jsonLd) ? jsonLd : [jsonLd]
}

function removeJsonLdScripts() {
  document.querySelectorAll(`[id^="${JSON_LD_SCRIPT_PREFIX}"]`).forEach((node) => node.remove())
}

function upsertMeta(selector: string, attrs: Record<string, string>, content: string) {
  let meta = document.querySelector(selector)
  if (!meta) {
    meta = document.createElement('meta')
    Object.entries(attrs).forEach(([key, value]) => meta!.setAttribute(key, value))
    document.head.appendChild(meta)
  }
  meta.setAttribute('content', content)
}

export default function SEOHead({
  title = SITE_CONFIG.title,
  description = SITE_CONFIG.description,
  keywords = DEFAULT_SEO_KEYWORDS,
  image = DEFAULT_OG_IMAGE,
  url = SITE_CONFIG.url,
  type = 'website',
  robots = DEFAULT_ROBOTS,
  favicon,
  jsonLd,
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title

    upsertMeta('meta[name="title"]', { name: 'title' }, title)
    upsertMeta('meta[name="description"]', { name: 'description' }, description)
    upsertMeta('meta[name="keywords"]', { name: 'keywords' }, keywords)
    upsertMeta('meta[name="robots"]', { name: 'robots' }, robots)

    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, title)
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, description)
    upsertMeta('meta[property="og:image"]', { property: 'og:image' }, image)
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, url)
    upsertMeta('meta[property="og:type"]', { property: 'og:type' }, type)

    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image')
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, title)
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, description)
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, image)
    upsertMeta('meta[name="twitter:url"]', { name: 'twitter:url' }, url)

    let canonical = document.querySelector('link[rel="canonical"]')
    if (!canonical) {
      canonical = document.createElement('link')
      canonical.setAttribute('rel', 'canonical')
      document.head.appendChild(canonical)
    }
    canonical.setAttribute('href', url)

    const faviconHref = favicon || '/favicon.png'
    let faviconLink = document.querySelector('link[rel="icon"]')
    if (!faviconLink) {
      faviconLink = document.createElement('link')
      faviconLink.setAttribute('rel', 'icon')
      document.head.appendChild(faviconLink)
    }
    faviconLink.setAttribute('type', 'image/png')
    faviconLink.setAttribute('href', faviconHref)

    removeJsonLdScripts()

    normalizeJsonLdBlocks(jsonLd).forEach((block, index) => {
      const script = document.createElement('script')
      script.id = `${JSON_LD_SCRIPT_PREFIX}-${index}`
      script.type = 'application/ld+json'
      script.textContent = JSON.stringify(block)
      document.head.appendChild(script)
    })

    return () => {
      removeJsonLdScripts()
    }
  }, [title, description, keywords, image, url, type, robots, favicon, jsonLd])

  return null
}
